import clsx from "clsx";
import React, { useState } from "react";
import {
  Await,
  type LoaderFunctionArgs,
  useAsyncValue,
  useLoaderData,
  useRevalidator,
} from "react-router";

import ApiErrorComponent from "@/components/common/api-error";
import FullPageSpinner from "@/components/common/full-page-spinner";
import { handled } from "@/helpers";
import { request } from "@/hooks/request";
import { showSnackbar } from "@/hooks/snackbar";
import type { Assignment, ExternalAssignment } from "@/types/assignment";

export function loader({ request: rr_request }: LoaderFunctionArgs) {
  const external_assignments = request<ExternalAssignment[]>("/api/external-assignment", {
    signal: rr_request.signal,
  });
  const assignments = request<Assignment[]>("/api/assignment", {
    signal: rr_request.signal,
  });
  return {
    external_assignments: external_assignments.then((res) => res.json),
    assignments: assignments.then((res) => res.json),
  };
}

export default function AssignmentImport() {
  const data = useLoaderData();
  return (
    <React.Suspense fallback={<FullPageSpinner />}>
      <Await
        resolve={Promise.all([data.external_assignments, data.assignments]).then(
          (values) => values,
        )}
        errorElement={<ApiErrorComponent />}
      >
        <AssignmentImportDeferred />
      </Await>
    </React.Suspense>
  );
}

function AssignmentImportDeferred() {
  const [external_assignments, assignments] = useAsyncValue() as [
    ExternalAssignment[],
    Assignment[],
  ];
  const imported = assignments.map((assignment) => assignment.external_id);

  return (
    <div className="mx-auto max-w-[60rem]">
      <div className="flex flex-col gap-2">
        {external_assignments.map((assignment) => {
          return (
            <ImportableExternalAssignment
              key={assignment.id}
              assignment={assignment}
              imported={imported.includes(String(assignment.id))}
            />
          );
        })}
      </div>
    </div>
  );
}

interface ImportableExternalAssignmentProps {
  assignment: ExternalAssignment;
  imported: boolean;
}

function ImportableExternalAssignment({ assignment, imported }: ImportableExternalAssignmentProps) {
  const revalidator = useRevalidator();
  const [is_submitting, setSubmitting] = useState(false);

  async function _import() {
    setSubmitting(true);
    const response = await request<{ error?: string }>(
      "/api/assignment/import",
      {
        method: "post",
        body: JSON.stringify(assignment),
      },
      true,
    );
    // A failed import is reported in the body as `error`, but a 4xx or 5xx
    // never gets that far, so the status is the other half of the check.
    const { error } = response.json;
    if (!response.is_ok || error) {
      console.error(error ?? response.status);
      showSnackbar(`Could not import ${assignment.name}.`, "warning");
      setSubmitting(false);
      return;
    }
    void revalidator.revalidate();
    setSubmitting(false);
  }

  const iconclass = clsx(
    "material-symbols-rounded notranslate cursor-pointer pr-2",
    imported && "text-green-500",
    !imported && "text-slate-400",
  );

  if (is_submitting) {
    return <div className="text-slate-400">Synchronizing assignment and candidates...</div>;
  }

  return (
    <div className="flex items-center">
      <button
        type="button"
        onClick={handled(_import)}
        className={iconclass}
        aria-label={`Import ${assignment.name}`}
      >
        cloud_sync
      </button>
      {assignment.name} - {assignment.organization}
    </div>
  );
}
